import { useEffect, useState, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Home, Gamepad2, User, Trophy, LayoutDashboard, ShoppingBag, FileText, Building2, MessageSquare } from 'lucide-react';
import { useAppData } from './hooks/useAppData';
import { supabase } from './supabaseClient';
import PatientConsent from './components/PatientConsent';
import BrandHeader from './components/BrandHeader';
import BridgeMark from './components/BridgeMark';
import BridgeNavIcon from './components/BridgeNavIcon';

// Real PatientLayout Shell
function PatientLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAppData();

  const navItems = [
    { id: 'profile', icon: User, label: 'פרופיל', path: '/patient/profile' },
    { id: 'store', icon: ShoppingBag, label: 'חנות', path: '/patient/store' },
    { id: 'games', icon: Gamepad2, label: 'משחקים', path: '/patient/games' },
    { id: 'bridges', icon: BridgeNavIcon, label: 'גשרים', path: '/patient/bridges' },
    { id: 'progress', icon: Trophy, label: 'התקדמות', path: '/patient/progress' },
    { id: 'home', icon: Home, label: 'בית', path: '/patient/home' },
  ];

  const handleDeclineConsent = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  };

  return (
    <>
      {profile && profile.role === 'patient' && !profile.privacy_accepted && (
        <PatientConsent 
          onAccept={() => window.location.reload()} 
          onDecline={handleDeclineConsent} 
        />
      )}
      <div className="flex flex-col h-full bg-background relative transition-colors duration-300">
        <BrandHeader />
        <div className="flex-1 overflow-hidden relative">
        <Routes>
          <Route path="home" element={<PatientHome />} />
          <Route path="progress" element={<ProgressPage />} />
          <Route path="bridges" element={<JourneyPage />} />
          <Route path="streak" element={<Navigate to="/patient/bridges" replace />} />
          <Route path="games" element={<MiniGames />} />
          <Route path="store" element={<Store />} />
          <Route path="profile" element={<PatientProfile />} />
          <Route path="*" element={<Navigate to="home" replace />} />
        </Routes>
      </div>

      {/* Bottom Navigation */}
      <div className="bg-card/90 backdrop-blur-md border-t border-border p-2 px-2 safe-area-pb">
        <div className="flex justify-between items-center bg-muted/50 rounded-2xl p-1">
            {navItems.map((item) => {
                const isActive = location.pathname.includes(item.id);
                return (
                    <button
                        key={item.id}
                        onClick={() => navigate(item.path)}
                        aria-current={isActive ? 'page' : undefined}
                        className={`flex flex-col items-center justify-center p-2 rounded-xl transition-all duration-300 w-full ${
                            isActive
                            ? 'text-primary bg-card shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        <item.icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                        <span className={`text-[11px] mt-1 ${isActive ? 'font-bold' : 'font-medium'}`}>{item.label}</span>
                    </button>
                );
            })}
        </div>
      </div>
    </div>
    </>
  );
}

// Real TherapistLayout Shell
function TherapistLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAppData();

  const navItems = [
    { id: 'profile', icon: User, label: 'פרופיל', path: '/therapist/profile' },
    { id: 'summaries', icon: FileText, label: 'סיכומים', path: '/therapist/summaries' },
    { id: 'community', icon: MessageSquare, label: 'קהילה', path: '/therapist/community' },
    { id: 'dashboard', icon: LayoutDashboard, label: 'מטופלים', path: '/therapist/dashboard' },
    { id: 'billing', icon: ShoppingBag, label: 'מנוי', path: '/therapist/billing' },
  ];

  if (profile?.is_clinic_admin) {
    navItems.push({ id: 'clinic-admin', icon: Building2, label: 'מרפאה', path: '/therapist/clinic-admin' });
  }

  // Desktop side menu order; the mobile bottom bar keeps navItems' visual order.
  const sideNavItems = ['dashboard', 'summaries', 'community', 'clinic-admin', 'billing', 'profile']
    .map((id) => navItems.find((item) => item.id === id))
    .filter((item): item is (typeof navItems)[number] => Boolean(item));

  return (
    <div className="flex h-full bg-background transition-colors duration-300 dir-rtl">
      {/* Desktop side menu */}
      <aside className="hidden md:flex w-60 shrink-0 flex-col bg-card border-e border-border">
        <div className="h-16 px-5 flex items-center gap-2 border-b border-border">
          <BridgeMark size={32} />
          <span className="text-primary font-extrabold text-lg tracking-tight">ReMind</span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {sideNavItems.map((item) => {
            const isActive = location.pathname.includes(item.id);
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <item.icon size={20} />
                {item.label}
              </button>
            );
          })}
        </nav>
        {profile?.full_name && (
          <div className="p-4 border-t border-border text-sm">
            <div className="text-muted-foreground text-xs">מחובר/ת כ</div>
            <div className="font-bold text-foreground truncate">{profile.full_name}</div>
          </div>
        )}
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <BrandHeader />
        <div className="flex-1 overflow-hidden">
          <div className="h-full w-full max-w-6xl mx-auto">
            <Routes>
              <Route path="dashboard" element={<TherapistDashboard />} />
              <Route path="summaries" element={<TherapistSummaries />} />
              <Route path="community" element={<TherapistCommunity />} />
              <Route path="profile" element={<TherapistProfile />} />
              <Route path="billing" element={<TherapistBilling />} />
              <Route path="clinic-admin" element={<ClinicAdminDashboard />} />
              <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Routes>
          </div>
        </div>

        {/* Mobile bottom navigation */}
        <div className="md:hidden bg-card border-t border-border p-2 safe-area-pb" dir="ltr">
          <div className="flex justify-around items-center">
              {navItems.map((item) => {
                  const isActive = location.pathname.includes(item.id);
                  return (
                      <button
                          key={item.id}
                          onClick={() => navigate(item.path)}
                          className={`flex flex-col items-center gap-1 p-3 rounded-xl transition-all w-32 ${
                              isActive ? 'text-primary bg-primary/10' : 'text-muted-foreground'
                          }`}
                      >
                          <item.icon size={24} />
                          <span className="text-xs font-bold">{item.label}</span>
                      </button>
                  );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}

import AuthPage from './pages/AuthPage';
import PatientHome from './pages/PatientHome';
import ProgressPage from './pages/ProgressPage';
import JourneyPage from './pages/JourneyPage';
import MiniGames from './pages/MiniGames';
import Store from './pages/Store';
import PatientProfile from './pages/PatientProfile';

import TherapistDashboard from './pages/TherapistDashboard';
import TherapistProfile from './pages/TherapistProfile';
import TherapistSummaries from './pages/TherapistSummaries';
import TherapistCommunity from './pages/TherapistCommunity';
import TherapistBilling from './pages/TherapistBilling';
import ClinicAdminDashboard from './pages/ClinicAdminDashboard';

import ChangeCredentials from './pages/ChangeCredentials';
import TherapistOnboarding from './pages/TherapistOnboarding';

import { useAppDataContext } from './context/AppDataContext';

export default function App() {
  const { user, profile, loading, debugStatus } = useAppDataContext();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Only the patient app is phone-shaped; therapist and sign-in screens use the full window.
  const isPatientArea = location.pathname.startsWith('/patient');

  // מונע ריצה כפולה של navigation
  const hasNavigated = useRef(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (loading) {
      hasNavigated.current = false;
      setIsReady(false);
      return;
    }

    // נתונים נטענו
    setIsReady(true);

    // מונע navigation מיותר
    if (hasNavigated.current) return;

    const currentPath = location.pathname;

    // 1️⃣ לא מחובר -> לדף התחברות
    if (!user) {
      if (currentPath !== '/auth') {
        console.log('Redirecting to /auth - no user');
        navigate('/auth', { replace: true });
        hasNavigated.current = true;
      }
      return;
    }

    // 2️⃣ לא טעון profile עדיין -> המתן
    if (!profile) {
      console.log('⏳ Waiting for profile to load...');
      return;
    }

    // 3️⃣ כניסה ראשונה -> Onboarding/שינוי סיסמה
    if (profile.first_login) {
      if (profile.role === 'patient') {
        if (currentPath !== '/change-credentials') {
          console.log('Redirecting to /change-credentials - first login');
          navigate('/change-credentials', { replace: true });
          hasNavigated.current = true;
        }
      } else if (profile.role === 'therapist') {
        if (currentPath !== '/therapist-onboarding') {
          console.log('Redirecting to /therapist-onboarding - first login');
          navigate('/therapist-onboarding', { replace: true });
          hasNavigated.current = true;
        }
      }
      return;
    }

    // 4️⃣ בדפי auth/setup אבל כבר התחבר (ולא first_login) -> לדשבורד המתאים
    if (currentPath === '/auth' || currentPath === '/change-credentials' || currentPath === '/therapist-onboarding') {
      const targetPath = profile.role === 'therapist' ? '/therapist/dashboard' : '/patient/home';
      console.log(`Redirecting to ${targetPath} - already logged in`);
      navigate(targetPath, { replace: true });
      hasNavigated.current = true;
      return;
    }

    // 5️⃣ בדף שגוי לפי role -> תקן
    if (profile.role === 'patient' && currentPath.startsWith('/therapist')) {
      console.log('Redirecting patient to /patient/home');
      navigate('/patient/home', { replace: true });
      hasNavigated.current = true;
      return;
    }

    if (profile.role === 'therapist' && currentPath.startsWith('/patient')) {
      console.log('Redirecting therapist to /therapist/dashboard');
      navigate('/therapist/dashboard', { replace: true });
      hasNavigated.current = true;
      return;
    }

    // 6️⃣ בשורש -> לדף הבית המתאים
    if (currentPath === '/') {
      const targetPath = profile.role === 'therapist' ? '/therapist/dashboard' : '/patient/home';
      console.log(`Redirecting from root to ${targetPath}`);
      navigate(targetPath, { replace: true });
      hasNavigated.current = true;
      return;
    }

    console.log('Navigation OK - user on correct page');
  }, [user, profile, loading, navigate, location.pathname]);

  // איפוס hasNavigated כשיוצרים logout
  useEffect(() => {
    if (!user) {
      hasNavigated.current = false;
    }
  }, [user]);

  if (loading || !isReady) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background text-foreground gap-4">
        <div className="text-2xl font-bold">טוען...</div>
        <div className="text-muted-foreground font-mono text-sm text-center px-4">
          {debugStatus || (user ? `User: ${user.email}` : 'Auth...')} 
          <br/>
          {typeof loading === 'string' ? loading : ''}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted flex justify-center font-sans">
      {/* Patient screens are built for phones, so on wide screens they stay a centered column. */}
      <div
        id="app-content"
        className={`w-full h-[100dvh] bg-background relative overflow-hidden flex flex-col ${
          isPatientArea ? 'max-w-[480px] sm:border-x sm:border-border sm:shadow-xl' : ''
        }`}
      >
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/change-credentials" element={<ChangeCredentials />} />
          <Route path="/therapist-onboarding" element={<TherapistOnboarding />} />
          
          <Route path="/patient/*" element={
            profile?.role === 'patient' ? <PatientLayout /> : <Navigate to="/auth" replace />
          } />

          <Route path="/therapist/*" element={
            profile?.role === 'therapist' ? <TherapistLayout /> : <Navigate to="/auth" replace />
          } />

          <Route path="*" element={
            <Navigate 
              to={profile?.role === 'therapist' ? '/therapist/dashboard' : '/patient/home'} 
              replace 
            />
          } />
        </Routes>
      </div>
    </div>
  );
}